/**
 * Calendar utilities for generating iCalendar (.ics) files
 */

export interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  startDate: Date;
  endDate: Date;
  organizer?: {
    name: string;
    email: string;
  };
}

/**
 * Format a date for iCalendar format (YYYYMMDDTHHMMSSZ)
 */
function formatICalDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
}

/**
 * Generate a unique identifier for the calendar event
 */
function generateUID(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}@cactuscuphema.com`;
}

/**
 * Escape special characters in iCalendar text fields
 */
function escapeICalText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Generate an iCalendar (.ics) file content for a tournament event
 */
export function generateTournamentCalendarInvite(event: CalendarEvent): string {
  const now = new Date();
  const dtstamp = formatICalDate(now);
  const dtstart = formatICalDate(event.startDate);
  const dtend = formatICalDate(event.endDate);
  const uid = generateUID();
  
  const title = escapeICalText(event.title);
  const description = event.description ? escapeICalText(event.description) : '';
  const location = event.location ? escapeICalText(event.location) : '';
  
  let organizerLine = '';
  if (event.organizer) {
    const organizerName = escapeICalText(event.organizer.name);
    organizerLine = `ORGANIZER;CN=${organizerName}:mailto:${event.organizer.email}\r\n`;
  }
  
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Cactus Cup//Tournament Registration//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${dtstart}`,
    `DTEND:${dtend}`,
    `SUMMARY:${title}`,
    description ? `DESCRIPTION:${description}` : '',
    location ? `LOCATION:${location}` : '',
    organizerLine,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(line => line !== '').join('\r\n');
  
  return icsContent;
}

/**
 * Generate a tournament calendar invite with default Cactus Cup settings
 */
export function generateCactusCupTournamentInvite(
  tournamentName: string,
  tournamentDate: string,
  experienceLevel?: string
): string {
  // Parse the tournament date
  const startDate = new Date(tournamentDate);
  
  // Set start time to 9:00 AM local time
  startDate.setHours(9, 0, 0, 0);
  
  // Set end time to 5:00 PM local time (8 hours later)
  const endDate = new Date(startDate);
  endDate.setHours(17, 0, 0, 0);
  
  // Build description
  let description = `You are registered for ${tournamentName} at Cactus Cup 2026.`;
  if (experienceLevel) {
    description += `\\n\\nDivision: ${experienceLevel}`;
  }
  description += '\\n\\nPlease arrive at least 1 hour before the tournament start time for check-in.';
  description += '\\n\\nImportant reminders:';
  description += '\\n- Complete your waiver form';
  description += '\\n- Provide emergency contact information';
  description += '\\n- Review tournament rules';
  description += '\\n- Check equipment requirements';
  
  return generateTournamentCalendarInvite({
    title: `${tournamentName} - Cactus Cup 2026`,
    description,
    location: 'Mesa, Arizona',
    startDate,
    endDate,
    organizer: {
      name: 'Cactus Cup',
      email: 'cactuscuphema@gmail.com'
    }
  });
}
